import type { AuditTrailEventDescriptionField } from '../model'

/** Configuration for a single field to diff within array items */
export interface ArrayDiffFieldConfig {
  /** Key on the item object to read */
  key: string
  /** Display label for the field */
  label: string
  /** Format the raw value for display; return null to skip */
  format: (value: unknown) => string | null
}

export interface ArrayDiffItemResult {
  /** The ID value for this item */
  id: string
  /** 'added' | 'removed' | 'updated' | 'unchanged' — overall status */
  status: 'added' | 'removed' | 'updated' | 'unchanged'
  /** Diff fields (only non-null entries) */
  fields: AuditTrailEventDescriptionField[]
}

export interface ArrayDiffResult {
  items: ArrayDiffItemResult[]
}

/**
 * Generic ID-based array diff utility.
 * Matches items by `idKey`, detects added/removed/updated.
 * For updated items, diffs individual scalar fields and returns only changes.
 * For added items, returns all non-null formatted fields.
 * For removed items, returns a marker with no fields.
 */
export function computeArrayDiff<T extends Record<string, unknown>>({
  currentItems,
  previousItems,
  idKey,
  fieldConfigs,
}: {
  currentItems: T[]
  previousItems: T[]
  idKey: keyof T
  fieldConfigs: ArrayDiffFieldConfig[]
}): ArrayDiffResult {
  const prevMap = new Map<string, T>()
  for (const item of previousItems) {
    const id = String(item[idKey])
    prevMap.set(id, item)
  }

  const currentIds = new Set<string>()
  const items: ArrayDiffItemResult[] = []

  // Process current items — detect added & updated
  for (const current of currentItems) {
    const id = String(current[idKey])
    currentIds.add(id)
    const previous = prevMap.get(id)

    if (!previous) {
      // Added item — show all non-null fields
      const fields: AuditTrailEventDescriptionField[] = []
      for (const config of fieldConfigs) {
        const formatted = config.format(current[config.key])
        if (formatted !== null) {
          fields.push({
            label: config.label,
            values: [{ diff: [formatted] }],
          })
        }
      }
      items.push({ id, status: 'added', fields })
    } else {
      // Potentially updated — diff each field
      const fields: AuditTrailEventDescriptionField[] = []
      for (const config of fieldConfigs) {
        const currentFormatted = config.format(current[config.key])
        const prevFormatted = config.format(previous[config.key])

        if (currentFormatted !== prevFormatted) {
          fields.push({
            label: config.label,
            values: [
              {
                previous: prevFormatted ?? '--',
                current: currentFormatted ?? '--',
              },
            ],
          })
        }
      }
      if (fields.length > 0) {
        items.push({ id, status: 'updated', fields })
      } else {
        items.push({ id, status: 'unchanged', fields: [] })
      }
    }
  }

  // Detect removed items — show all previous values
  for (const prev of previousItems) {
    const id = String(prev[idKey])
    if (!currentIds.has(id)) {
      const fields: AuditTrailEventDescriptionField[] = []
      for (const config of fieldConfigs) {
        const formatted = config.format(prev[config.key])
        if (formatted !== null) {
          fields.push({
            label: config.label,
            values: [{ diff: [formatted] }],
            action: 'removed',
          })
        }
      }
      items.push({ id, status: 'removed', fields })
    }
  }

  return { items }
}

/**
 * Variant for first-time events (no previous data).
 * Returns all non-null formatted fields for each item.
 */
export function computeArrayInitial<T extends Record<string, unknown>>({
  items: sourceItems,
  idKey,
  fieldConfigs,
}: {
  items: T[]
  idKey: keyof T
  fieldConfigs: ArrayDiffFieldConfig[]
}): ArrayDiffResult {
  const items: ArrayDiffItemResult[] = []

  for (const item of sourceItems) {
    const id = String(item[idKey])
    const fields: AuditTrailEventDescriptionField[] = []
    for (const config of fieldConfigs) {
      const formatted = config.format(item[config.key])
      if (formatted !== null) {
        fields.push({
          label: config.label,
          values: [{ diff: [formatted] }],
        })
      }
    }
    items.push({ id, status: 'added', fields })
  }

  return { items }
}
